import React, { useEffect, useMemo, useState } from 'react';
import { supabase } from '../lib/supabase';
import { LoadingSkeleton } from '../components/LoadingSkeleton';
import { Pagination } from '../components/Pagination';
import { ShieldAlert, Filter } from 'lucide-react';

type CompanyOption = {
  id: string;
  label: string;
};

const COMPANY_OPTIONS: CompanyOption[] = [
  { id: '0f88534f-2ba8-412b-a554-c55ef5fdcf0e', label: 'Hales AC' },
  { id: '1212061e-f319-406e-b5c6-ebcb4c46e9d6', label: 'Climate Control (MyCCS)' },
  { id: '2ce92bf5-062b-4e8d-9ba3-4efc45a31921', label: 'Abraham AC' },
];

const COMPANY_MAP = Object.fromEntries(COMPANY_OPTIONS.map((c) => [c.id, c.label]));

interface LinkedCustomer {
  company_id: string;
  first_name: string | null;
  last_name: string | null;
  company_name: string | null;
  email: string | null;
  phone_number: string | null;
}

interface DNCEntry {
  id: string;
  customer_id: string | null;
  channel: string;
  reason: string | null;
  source: string | null;
  added_by: string | null;
  added_at: string;
  revoked_at: string | null;
  sw_customers?: LinkedCustomer | null;
}

const PAGE_SIZE = 50;

export default function DNCPage() {
  const [entries, setEntries] = useState<DNCEntry[]>([]);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(true);

  const [channel, setChannel] = useState<string>('');
  const [activeOnly, setActiveOnly] = useState<boolean>(true);
  const [page, setPage] = useState<number>(1);

  useEffect(() => {
    fetchDnc();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [channel, activeOnly, page]);

  const fetchDnc = async () => {
    try {
      setLoading(true);
      const from = (page - 1) * PAGE_SIZE;
      const to = from + PAGE_SIZE - 1;

      let query = supabase
        .from('customer_do_not_contact')
        .select(
          `id, customer_id, channel, reason, source, added_by, added_at, revoked_at, sw_customers:customer_id(company_id, first_name, last_name, company_name, email, phone_number)`,
          { count: 'exact' }
        )
        .order('added_at', { ascending: false })
        .range(from, to);

      if (activeOnly) {
        query = query.is('revoked_at', null);
      }

      if (channel) {
        query = query.eq('channel', channel);
      }

      const { data, error, count } = await query;
      if (error) throw error;

      const rows = (data ?? []) as Array<
        Omit<DNCEntry, 'sw_customers'> & { sw_customers?: LinkedCustomer | LinkedCustomer[] | null }
      >;

      const normalized: DNCEntry[] = rows.map((row) => ({
        ...row,
        sw_customers: Array.isArray(row.sw_customers)
          ? row.sw_customers[0] ?? null
          : row.sw_customers ?? null,
      }));

      setEntries(normalized);
      setTotalCount(count ?? 0);
    } catch (err) {
      console.error('Failed to load DNC entries', err?.message || err);
      setEntries([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  };

  const totalPages = Math.max(1, Math.ceil((totalCount || entries.length) / PAGE_SIZE));

  const activeCount = useMemo(
    () => entries.filter((e) => !e.revoked_at).length,
    [entries]
  );

  const renderCompany = (e: DNCEntry) => {
    const cid = e.sw_customers?.company_id;
    if (!cid) return '—';
    return COMPANY_MAP[cid] || cid;
  };

  const renderName = (e: DNCEntry) => {
    const c = e.sw_customers;
    if (!c) return e.customer_id || '—';

    const parts = [c.first_name, c.last_name].filter(Boolean);
    const name = parts.join(' ');

    if (name) return name;
    if (c.company_name) return c.company_name;
    return e.customer_id || '—';
  };

  return (
    <div className="p-8">
      <div className="flex items-start justify-between gap-6 mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
            <ShieldAlert className="text-red-600" />
            Do Not Contact
          </h1>
          <p className="text-gray-600 mt-2">
            Active opt-outs across email/SMS/phone. This list is also queryable via the DNC API.
          </p>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm min-w-[260px]">
          <div className="text-sm text-gray-600">Showing</div>
          <div className="text-2xl font-bold text-gray-900">{activeOnly ? activeCount : entries.length}</div>
          <div className="text-xs text-gray-500 mt-1">
            {activeOnly ? 'active entries on this page' : 'entries on this page'}
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm mb-6">
        <div className="flex items-center gap-4 flex-wrap">
          <div className="flex items-center gap-2 text-gray-700">
            <Filter size={18} />
            <span className="font-medium">Filters</span>
          </div>

          <div className="flex items-center gap-2">
            <label className="text-sm text-gray-600">Channel</label>
            <select
              value={channel}
              onChange={(e) => {
                setChannel(e.target.value);
                setPage(1);
              }}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
            >
              <option value="">All</option>
              <option value="email">Email</option>
              <option value="sms">SMS</option>
              <option value="phone">Phone</option>
            </select>
          </div>

          <label className="flex items-center gap-2 text-sm text-gray-700">
            <input
              type="checkbox"
              checked={activeOnly}
              onChange={(e) => {
                setActiveOnly(e.target.checked);
                setPage(1);
              }}
            />
            Active only
          </label>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        {loading ? (
          <div className="p-6">
            <LoadingSkeleton count={8} />
          </div>
        ) : entries.length === 0 ? (
          <div className="p-12 text-center">
            <ShieldAlert className="mx-auto text-gray-300" size={48} />
            <h3 className="mt-4 text-lg font-semibold text-gray-900">No DNC entries found</h3>
            <p className="mt-2 text-gray-600">
              When customers are opted out, they’ll appear here.
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Added
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Channel
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Company
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Customer
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Email
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Phone
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Reason
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {entries.map((e) => (
                  <tr key={e.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                      {new Date(e.added_at).toLocaleString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                        {e.channel}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                      {renderCompany(e)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-medium">
                      {renderName(e)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                      {e.sw_customers?.email || '—'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                      {e.sw_customers?.phone_number || '—'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 max-w-[340px] truncate">
                      {e.reason || '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {!loading && totalPages > 1 && (
          <div className="border-t border-gray-200">
            <Pagination currentPage={page} totalPages={totalPages} onPageChange={setPage} />
          </div>
        )}
      </div>
    </div>
  );
}
